import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Avatar, Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import biApi from "../../../../../api/biApi.ts";
import { WidgetType } from "../../../../../api/types/dashboardsTypes.ts";
import type { ReportInfo } from "../../../../../api/types/svTypes.ts";
import getReportTypeIcon from "../../../reporting/sv/components/common/visualization/icons/getReportTypeIcon.tsx";
import TabularIcon from "../../../reporting/sv/components/common/visualization/icons/TabularIcon.tsx";
import type { TileObject } from "../../models/TileObject";
import { generateRandomString } from "../../utils/generateRandomString";
import { smartViewWidgetReportTypes } from "../../widgets/smartview/utils/smartViewWidgetReportTypes.ts";
import { defaultSmartViewWidgetSize } from "./generateWidgetId";
import { reportItemSx } from "./reportItemSx";

interface Props {
  search: string;
}

const SmartViewReports = ({ search }: Props) => {
  const fetchReports = useCallback((signal?: AbortSignal) => biApi.uiReports.getReports({}, signal), []);

  const [reports, fetchReportsError, { isFetching: isFetchingReports }] = useFetch(fetchReports);

  const [expanded, toggleExpanded] = useToggleState(true);

  const filteredReports = useMemo(
    () =>
      (reports ?? []).filter(
        (report) =>
          smartViewWidgetReportTypes.includes(report.reportType) &&
          report.name.toLowerCase().includes(search.toLowerCase())
      ),
    [reports, search]
  );

  if (isFetchingReports && !fetchReportsError) {
    return <InlineLoader />;
  }

  if (fetchReportsError) {
    return <DataLoadingFailed text="Error fetching reports" />;
  }

  return (
    <Stack spacing={1} useFlexGap sx={{ flex: 1 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle1">SmartViews Reports</Typography>
        {expanded ? (
          <IconButton onClick={toggleExpanded}>
            <ExpandLessRoundedIcon />
          </IconButton>
        ) : (
          <IconButton onClick={toggleExpanded}>
            <ExpandMoreRoundedIcon />
          </IconButton>
        )}
      </Stack>
      <Collapse orientation="vertical" in={expanded} sx={{ "& .MuiCollapse-wrapperInner": { display: "block" } }}>
        <Stack spacing={1} useFlexGap sx={{ flex: 1, px: 0.5, minHeight: "0" }}>
          {filteredReports.map((report) => (
            <Box
              key={report.reportId}
              sx={reportItemSx}
              className="droppable-element"
              draggable={true}
              data-type="smartView"
              onDragStart={(e) => handleDragStart(e, report)}
            >
              <Avatar sx={{ width: 32, height: 32, backgroundColor: "action.hover", borderRadius: 1 }}>
                <Box sx={{ display: "inline-flex" }}>{getReportTypeIcon(report.reportType) ?? <TabularIcon />}</Box>
              </Avatar>
              <Stack direction="column">
                <Typography variant="body1">{report.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {report.reportType}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

const handleDragStart = (e: React.DragEvent, report: ReportInfo) => {
  const id = "sv_" + generateRandomString();
  e.dataTransfer.setData(
    "application/json",
    JSON.stringify({
      id,
      item: {
        tileId: id,
        location: {
          row: 0,
          column: 0,
          width: defaultSmartViewWidgetSize.width,
          height: defaultSmartViewWidgetSize.height,
        },
        widget: {
          widgetId: id,
          type: WidgetType.SmartView,
          reportId: report.reportId,
        },
      },
    } as Partial<TileObject>)
  );

  e.dataTransfer.setData(WidgetType.SmartView.toLowerCase(), "");
};

export default SmartViewReports;
